import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { resolveWidgetMediaUrl } from '../lib/resolveWidgetMediaUrl';
import type { DemoAttachment } from './groupConsecutiveImageMessages';

type Props = {
  images: DemoAttachment[];
  /** Max width of the album grid */
  maxWidth?: number;
};

/**
 * WhatsApp-style image album: up to 4 tiles, +N overlay on the 4th when more exist.
 * Click opens a simple lightbox gallery.
 */
export function DemoImageAlbum({ images, maxWidth = 280 }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const resolved = images
    .map((a) => ({ ...a, src: resolveWidgetMediaUrl(a.url) }))
    .filter((a) => Boolean(a.src));

  const close = useCallback(() => setOpenIndex(null), []);
  const showPrev = useCallback(() => {
    setOpenIndex((i) => (i == null ? i : (i - 1 + resolved.length) % resolved.length));
  }, [resolved.length]);
  const showNext = useCallback(() => {
    setOpenIndex((i) => (i == null ? i : (i + 1) % resolved.length));
  }, [resolved.length]);

  useEffect(() => {
    if (openIndex == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, close, showPrev, showNext]);

  if (resolved.length === 0) return null;

  const total = resolved.length;
  const overflow = total > 4 ? total - 3 : 0;
  const tiles =
    total === 1 ? resolved.slice(0, 1)
    : total === 2 ? resolved.slice(0, 2)
    : total === 3 ? resolved.slice(0, 3)
    : resolved.slice(0, 4);

  const gridTemplate =
    total === 1 ? '1fr'
    : total === 2 ? '1fr 1fr'
    : '1fr 1fr';

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          gap: 2,
          maxWidth,
          width: '100%',
          borderRadius: 10,
          overflow: 'hidden',
          marginTop: 8,
        }}
      >
        {tiles.map((tile, idx) => {
          const isOverflowTile = overflow > 0 && idx === 3;
          const openAt = isOverflowTile ? 3 : idx;
          const tall = total === 1;
          const spanFull =
            total === 3 && idx === 2
              ? true
              : false;
          return (
            <button
              key={`${tile.src}-${idx}`}
              type="button"
              onClick={() => setOpenIndex(openAt)}
              aria-label={
                isOverflowTile ? `Ver ${overflow} fotos más` : tile.caption || `Foto ${idx + 1}`
              }
              style={{
                position: 'relative',
                padding: 0,
                border: 'none',
                cursor: 'pointer',
                background: '#0f172a',
                aspectRatio: tall ? '4 / 3' : '1',
                gridColumn: spanFull ? '1 / -1' : undefined,
                maxHeight: tall ? 220 : undefined,
                overflow: 'hidden',
              }}
            >
              <img
                src={tile.src}
                alt={tile.caption || 'Foto'}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              {isOverflowTile ? (
                <span
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(15, 23, 42, 0.55)',
                    color: '#fff',
                    fontSize: 28,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                  }}
                >
                  +{overflow}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {openIndex != null && resolved[openIndex] ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Galería de fotos"
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(15, 23, 42, 0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Cerrar"
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              border: 'none',
              background: 'transparent',
              color: '#fff',
              fontSize: 28,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>
          {resolved.length > 1 ? (
            <>
              <button
                type="button"
                aria-label="Anterior"
                onClick={(e) => {
                  e.stopPropagation();
                  showPrev();
                }}
                style={navBtnStyle('left')}
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Siguiente"
                onClick={(e) => {
                  e.stopPropagation();
                  showNext();
                }}
                style={navBtnStyle('right')}
              >
                ›
              </button>
            </>
          ) : null}
          <img
            src={resolved[openIndex]!.src}
            alt={resolved[openIndex]!.caption || 'Foto'}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 'min(920px, 100%)',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 8,
            }}
          />
          <span
            style={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'rgba(255,255,255,0.85)',
              fontSize: 13,
            }}
          >
            {openIndex + 1} / {resolved.length}
          </span>
        </div>
      ) : null}
    </>
  );
}

function navBtnStyle(side: 'left' | 'right'): CSSProperties {
  return {
    position: 'absolute',
    [side]: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    border: 'none',
    background: 'rgba(255,255,255,0.12)',
    color: '#fff',
    fontSize: 36,
    width: 44,
    height: 44,
    borderRadius: 22,
    cursor: 'pointer',
    lineHeight: 1,
  };
}
